import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ImagePlus, X, Loader2, MapPin, CreditCard, Package, Truck, MessageCircle, Coins, Wallet, AlertTriangle, Video, Play } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api, { API_URL } from '../lib/api';

const CATEGORIES = ['Eletrônicos', 'Veículos', 'Imóveis', 'Moda', 'Casa e Decoração', 'Esportes', 'Livros', 'Serviços', 'Digital', 'Outros'];

export default function EditListingPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    title: '', description: '', price: '', category: '', subcategory: '',
    state: '', city: '', condition: 'used', product_type: 'physical',
    installment_policy: 'buyer_pays_all',
  });
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [payoutMethod, setPayoutMethod] = useState('credits');
  const [pixKey, setPixKey] = useState('');
  const [userPixKey, setUserPixKey] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [hideWhenSoldOut, setHideWhenSoldOut] = useState(true);
  const [videoPath, setVideoPath] = useState(null);
  const [videoPlan, setVideoPlan] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [error, setError] = useState('');
  const [platformFeePercent, setPlatformFeePercent] = useState(6);

  useEffect(() => {
    api.get('/locations/states').then(r => setStates(r.data.states)).catch(() => {});
    api.get('/config/platform-fee').then(r => setPlatformFeePercent(r.data.platform_fee_percent)).catch(() => {});
    api.get('/auth/me').then(r => {
      const pk = r.data.user?.pix_key || '';
      setUserPixKey(pk);
      setPixKey(pk);
    }).catch(() => {});
  }, []);

  // Load existing product
  useEffect(() => {
    const loadProduct = async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        const p = data.product;
        if (p.seller_id !== user?._id && user?.role !== 'admin') {
          toast.error('Sem permissão para editar este anúncio');
          navigate('/dashboard');
          return;
        }
        setForm({
          title: p.title || '',
          description: p.description || '',
          price: String(p.price || ''),
          category: p.category || '',
          subcategory: p.subcategory || '',
          state: p.state || '',
          city: p.city || '',
          condition: p.condition || 'used',
          product_type: p.product_type || 'physical',
          installment_policy: p.installment_policy || 'buyer_pays_all',
        });
        setDeliveryOptions(p.delivery_options || []);
        setPayoutMethod(p.payout_method || 'credits');
        setQuantity(p.quantity || 1);
        setHideWhenSoldOut(p.hide_when_sold_out !== false);
        setVideoPath(p.video || null);
        setVideoPlan(p.video_plan || null);
        if (p.images?.length) {
          setImages(p.images.map(path => ({
            path,
            url: `/api/files/${path}`,
            preview: path.startsWith('http') ? path : `${API_URL}/api/files/${path}`,
          })));
        }
      } catch {
        toast.error('Erro ao carregar anúncio');
        navigate('/dashboard');
      } finally {
        setLoadingProduct(false);
      }
    };
    if (user) loadProduct();
  }, [id, user, navigate]);

  useEffect(() => {
    if (form.state) {
      api.get(`/locations/cities/${form.state}`).then(r => setCities(r.data.cities)).catch(() => setCities([]));
    } else {
      setCities([]);
    }
  }, [form.state]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 8) { setError('Máximo de 8 imagens'); return; }
    setUploading(true);
    setError('');
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        setImages(prev => [...prev, { path: data.path, url: data.url, preview: URL.createObjectURL(file) }]);
      } catch (err) {
        setError(err.response?.data?.detail || 'Erro ao enviar imagem');
      }
    }
    setUploading(false);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['video/mp4', 'video/quicktime', 'video/webm'].includes(file.type)) {
      toast.error('Formato não suportado. Use MP4, MOV ou WebM.');
      return;
    }
    const plan = videoPlan || 'basic';
    const maxSize = plan === 'premium' ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Vídeo muito grande. Máximo ${maxSize / (1024*1024)}MB para este plano.`);
      return;
    }
    setVideoFile(file);
  };

  const uploadVideo = async () => {
    if (!videoFile || !videoPlan) return null;
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', videoFile);
      const { data } = await api.post(`/upload/video?plan=${videoPlan}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setVideoPath(data.path);
      toast.success(`Vídeo enviado! R$ ${data.price.toFixed(2)} descontados.`);
      return data.path;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar vídeo');
      return null;
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title || !form.description || !form.price || !form.category) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }
    setSubmitting(true);
    try {
      let finalVideoPath = videoPath;
      if (videoFile && videoPlan && !videoPath) {
        finalVideoPath = await uploadVideo();
        if (videoFile && videoPlan && !finalVideoPath) {
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        ...form,
        price: parseFloat(form.price),
        images: images.map(i => i.path),
        delivery_options: deliveryOptions,
        payout_method: payoutMethod,
        quantity: Math.max(1, parseInt(quantity) || 1),
        hide_when_sold_out: hideWhenSoldOut,
        video: finalVideoPath || null,
        video_plan: videoPlan,
      };

      if (payoutMethod === 'pix' && pixKey && pixKey !== userPixKey) {
        await api.put('/auth/profile', { pix_key: pixKey }).catch(() => {});
      }
      await api.put(`/products/${id}`, payload);
      toast.success('Anúncio atualizado com sucesso!');
      navigate(`/products/${id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar anúncio');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) { navigate('/login'); return null; }

  if (loadingProduct) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="space-y-4">
          <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
          <div className="h-40 bg-muted rounded animate-pulse" />
          <div className="h-10 bg-muted rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="edit-listing-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>Editar Anúncio</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="edit-error">{error}</div>}

        {/* Images */}
        <div>
          <Label className="mb-2 block">Fotos (até 8)</Label>
          <div className="grid grid-cols-4 gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted group">
                <img src={img.preview} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {images.length < 8 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                data-testid="upload-image-btn"
              >
                {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
                <span className="text-[10px]">{uploading ? 'Enviando...' : 'Adicionar'}</span>
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-testid="file-input" />
        </div>

        {/* Video Upload */}
        <div className="space-y-3 p-5 rounded-xl border border-border bg-card">
          <Label className="flex items-center gap-1.5"><Video className="h-4 w-4 text-primary" /> Vídeo <Badge variant="outline" className="ml-1 text-[10px]">Pago</Badge></Label>

          {videoPath && !videoFile && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
              <Play className="h-5 w-5 text-green-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-700">Vídeo já associado</p>
                {videoPlan && <p className="text-xs text-green-600">Plano {videoPlan === 'premium' ? '30s' : '15s'}</p>}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setVideoPath(null); setVideoPlan(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {!videoFile && !videoPath && (
            <>
              <p className="text-xs text-muted-foreground">Adicione ou substitua o vídeo. O valor é descontado dos seus créditos.</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'basic', label: 'Até 15 segundos', size: '50MB', price: 'R$ 2,50' },
                  { id: 'premium', label: 'Até 30 segundos', size: '100MB', price: 'R$ 5,00' },
                ].map(p => (
                  <button key={p.id} type="button"
                    onClick={() => { setVideoPlan(p.id); document.getElementById('video-input-edit')?.click(); }}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:border-primary/50 ${videoPlan === p.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                    data-testid={`video-plan-${p.id}`}
                  >
                    <p className="text-sm font-semibold flex items-center gap-1.5"><Play className="h-3.5 w-3.5" /> {p.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">Máx {p.size} &middot; <span className="font-medium text-primary">{p.price}</span></p>
                  </button>
                ))}
              </div>
            </>
          )}

          <input id="video-input-edit" type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden"
            onChange={handleVideoSelect} data-testid="video-file-input" />

          {videoFile && !videoPath && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
              <Video className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{videoFile.name}</p>
                <p className="text-xs text-muted-foreground">{(videoFile.size / (1024*1024)).toFixed(1)}MB &middot; Plano: {videoPlan === 'premium' ? '30s' : '15s'}</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setVideoFile(null); setVideoPlan(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {uploadingVideo && (
            <div className="flex items-center gap-2 text-sm text-primary">
              <Loader2 className="h-4 w-4 animate-spin" /> Enviando vídeo...
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="title">Título *</Label>
          <Input id="title" placeholder="Ex: iPhone 15 Pro Max 256GB" value={form.title} onChange={e => update('title', e.target.value)} required data-testid="listing-title-input" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Descrição *</Label>
          <Textarea id="description" placeholder="Descreva seu produto com detalhes..." value={form.description} onChange={e => update('description', e.target.value)} rows={4} required data-testid="listing-description-input" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="price">Preço (R$) *</Label>
            <Input id="price" type="number" step="0.01" placeholder="0,00" value={form.price} onChange={e => update('price', e.target.value)} required data-testid="listing-price-input" />
            {form.price && parseFloat(form.price) > 0 && (
              <div className="text-xs text-green-700 mt-1 p-2 bg-green-50 rounded-md border border-green-200" data-testid="fee-estimate">
                <p>Sem taxas de negociação! <span className="font-semibold">Você recebe 100% do valor da venda.</span></p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Categoria *</Label>
            <Select value={form.category} onValueChange={v => update('category', v)}>
              <SelectTrigger data-testid="listing-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Condição</Label>
            <Select value={form.condition} onValueChange={v => update('condition', v)}>
              <SelectTrigger data-testid="listing-condition-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Novo</SelectItem>
                <SelectItem value="used">Usado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={form.product_type} onValueChange={v => update('product_type', v)}>
              <SelectTrigger data-testid="listing-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="physical">Físico</SelectItem>
                <SelectItem value="digital">Digital</SelectItem>
                <SelectItem value="service">Serviço</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3">
          <Label className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" /> Localização</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Estado</Label>
              <Select value={form.state} onValueChange={v => { update('state', v); update('city', ''); }}>
                <SelectTrigger data-testid="listing-state-select"><SelectValue placeholder="Selecione o estado" /></SelectTrigger>
                <SelectContent>
                  {states.map(s => <SelectItem key={s.uf} value={s.uf}>{s.name} ({s.uf})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Cidade</Label>
              <Select value={form.city} onValueChange={v => update('city', v)} disabled={!form.state}>
                <SelectTrigger data-testid="listing-city-select"><SelectValue placeholder={form.state ? "Selecione a cidade" : "Selecione o estado primeiro"} /></SelectTrigger>
                <SelectContent>
                  {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Quantity */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="quantity" className="flex items-center gap-1.5"><Package className="h-4 w-4 text-primary" /> Quantidade disponível</Label>
            <Input id="quantity" type="number" min="1" value={quantity}
              onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              data-testid="listing-quantity-input" />
          </div>
          <div className="space-y-2 flex flex-col justify-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer p-2.5 rounded-lg border border-border hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={!hideWhenSoldOut}
                onCheckedChange={(checked) => setHideWhenSoldOut(!checked)}
                data-testid="keep-visible-checkbox"
              />
              <span className="text-muted-foreground">Manter visível quando esgotar</span>
            </label>
          </div>
        </div>

        {/* Delivery Options */}
        <div className="space-y-3 p-5 rounded-xl border border-border bg-card">
          <Label className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-primary" /> Opções de Entrega</Label>
          <div className="space-y-3">
            {[
              { id: 'seller_ships', icon: Package, label: 'Vendedor envia', desc: 'Você se responsabiliza pelo envio do produto ao comprador.' },
              { id: 'buyer_picks_up', icon: Truck, label: 'Comprador retira', desc: 'O comprador se responsabiliza pela retirada do produto.' },
              { id: 'local_only', icon: MapPin, label: 'Retirar no local', desc: 'O produto só pode ser retirado no endereço do vendedor.' },
              { id: 'chat_arrange', icon: MessageCircle, label: 'Combinar pelo chat', desc: 'Comprador e vendedor combinam a entrega ou retirada pelo chat.' },
            ].map(opt => (
              <label key={opt.id} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${deliveryOptions.includes(opt.id) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}>
                <Checkbox
                  checked={deliveryOptions.includes(opt.id)}
                  onCheckedChange={(checked) => {
                    setDeliveryOptions(prev => checked ? [...prev, opt.id] : prev.filter(o => o !== opt.id));
                  }}
                  className="mt-0.5"
                  data-testid={`delivery-${opt.id}`}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium flex items-center gap-1.5"><opt.icon className="h-3.5 w-3.5 text-muted-foreground" /> {opt.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Payout Method */}
        <div className="space-y-3 p-5 rounded-xl border border-border bg-card">
          <Label className="flex items-center gap-1.5"><Wallet className="h-4 w-4 text-primary" /> Como deseja receber o valor da venda?</Label>
          <div className="space-y-3">
            <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${payoutMethod === 'credits' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}>
              <input type="radio" name="payout" value="credits" checked={payoutMethod === 'credits'}
                onChange={() => setPayoutMethod('credits')} className="mt-1" data-testid="payout-credits" />
              <div className="flex-1">
                <p className="text-sm font-medium flex items-center gap-1.5"><Coins className="h-3.5 w-3.5 text-amber-500" /> Receber como créditos na plataforma</p>
                <p className="text-xs text-muted-foreground mt-0.5">O valor é adicionado automaticamente ao seu saldo de créditos.</p>
              </div>
            </label>
            <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${payoutMethod === 'pix' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}>
              <input type="radio" name="payout" value="pix" checked={payoutMethod === 'pix'}
                onChange={() => setPayoutMethod('pix')} className="mt-1" data-testid="payout-pix" />
              <div className="flex-1">
                <p className="text-sm font-medium flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5 text-teal-600" /> Receber via PIX</p>
                <p className="text-xs text-muted-foreground mt-0.5">Depósito na sua chave PIX em até 24 horas após confirmação.</p>
              </div>
            </label>
            {payoutMethod === 'pix' && (
              <div className="ml-7 space-y-2">
                <Input placeholder="Sua chave PIX" value={pixKey} onChange={e => setPixKey(e.target.value)} data-testid="pix-key-input" />
                {!pixKey && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Cadastre sua chave PIX para receber pagamentos.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Installment Policy */}
        <div className="space-y-3 p-5 rounded-xl border border-border bg-card">
          <Label className="flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-primary" /> Parcelamento no Cartão</Label>
          <div className="space-y-3">
            <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${form.installment_policy === 'no_installments' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}>
              <input type="radio" name="installment_policy" value="no_installments" checked={form.installment_policy === 'no_installments'} onChange={() => update('installment_policy', 'no_installments')} className="mt-1" data-testid="policy-no-installments" />
              <div>
                <p className="text-sm font-medium">Apenas à vista (sem parcelamento)</p>
                <p className="text-xs text-muted-foreground mt-0.5">O comprador paga o valor total de uma só vez no cartão, sem juros e sem parcelamento.</p>
              </div>
            </label>
            <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${form.installment_policy === 'buyer_pays_all' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}>
              <input type="radio" name="installment_policy" value="buyer_pays_all" checked={form.installment_policy === 'buyer_pays_all'} onChange={() => update('installment_policy', 'buyer_pays_all')} className="mt-1" data-testid="policy-buyer-pays" />
              <div>
                <p className="text-sm font-medium">Comprador paga os juros (até 12x)</p>
                <p className="text-xs text-muted-foreground mt-0.5">O comprador escolhe livremente quantas parcelas quer (1x a 12x). Juros a partir da 2a parcela. Você recebe o valor integral.</p>
              </div>
            </label>
            <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${form.installment_policy === 'seller_absorbs_3x' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}>
              <input type="radio" name="installment_policy" value="seller_absorbs_3x" checked={form.installment_policy === 'seller_absorbs_3x'} onChange={() => update('installment_policy', 'seller_absorbs_3x')} className="mt-1" data-testid="policy-seller-absorbs" />
              <div>
                <p className="text-sm font-medium">Sem juros até 3x (você absorve)</p>
                <p className="text-xs text-muted-foreground mt-0.5">O comprador parcela em até 3x sem juros — você absorve o custo. A partir da 4a parcela, juros de mercado.</p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="outline" className="flex-1 rounded-lg" onClick={() => navigate(`/products/${id}`)} data-testid="cancel-edit-btn">
            Cancelar
          </Button>
          <Button type="submit" className="flex-1 rounded-lg" disabled={submitting} data-testid="save-edit-btn">
            {submitting ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </form>
    </div>
  );
}
